<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import PermissionSetForm from './PermissionSetForm.vue';
import PermissionSetList from './PermissionSetList.vue';
import { usePermissionSetManagement } from './usePermissionSetManagement';

const {
  management,
  allowed,
  editor,
  deleting,
  error,
  notice,
  busy,
  reload,
  save,
  remove,
} = usePermissionSetManagement();
const add = () => {
  editor.value = { name: '', description: '', permissions: [] };
};
</script>

<template>
  <section class="flex w-full flex-col gap-5">
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ $t('PERMISSION_SETS.TITLE') }}
        </h1>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('PERMISSION_SETS.INTRO') }}
        </p>
      </div>
      <Button
        v-if="allowed && management.catalog && !editor"
        :label="$t('PERMISSION_SETS.ADD')"
        :disabled="busy || deleting !== null"
        @click="add"
      />
    </header>
    <p v-if="!allowed" role="status" class="text-n-slate-11">
      {{ $t('PERMISSION_SETS.UNAVAILABLE') }}
    </p>
    <template v-else>
      <p v-if="error" role="alert" class="mb-0 text-n-ruby-11">{{ error }}</p>
      <p v-if="notice" role="status" class="mb-0 text-n-teal-11">
        {{ notice }}
      </p>
      <p v-if="management.loading" role="status" class="mb-0 text-n-slate-11">
        {{ $t('PERMISSION_SETS.LOADING') }}
      </p>
      <Button
        v-if="error"
        variant="outline"
        :label="$t('PERMISSION_SETS.RETRY')"
        :disabled="busy"
        @click="reload"
      />
      <PermissionSetForm
        v-if="editor && management.catalog"
        :key="editor.id || 'new'"
        :record="editor"
        :catalog="management.catalog"
        :busy="busy"
        @save="save"
        @cancel="editor = null"
      />
      <template v-else>
        <PermissionSetList
          v-if="management.records.length"
          :records="management.records"
          :busy="busy || deleting !== null"
          @edit="editor = $event"
          @remove="deleting = $event"
        />
        <p v-else-if="!busy && !error" class="text-n-slate-11">
          {{ $t('PERMISSION_SETS.EMPTY') }}
        </p>
      </template>
      <section
        v-if="deleting"
        class="flex flex-col gap-3 rounded border border-n-weak p-4"
      >
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('PERMISSION_SETS.CONFIRM_DELETE', { name: deleting.name }) }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('PERMISSION_SETS.DELETE_HELP') }}
        </p>
        <div class="flex gap-3">
          <Button
            color="ruby"
            :label="$t('PERMISSION_SETS.DELETE')"
            :disabled="busy"
            @click="remove"
          />
          <Button
            variant="ghost"
            :label="$t('PERMISSION_SETS.CANCEL')"
            :disabled="busy"
            @click="deleting = null"
          />
        </div>
      </section>
    </template>
  </section>
</template>
